import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { AssistantService, IndexService, CitationModel, MessageRole } from 'digiteq-ai-portal-client-lib';
import { FeMessageModel } from '../../models/fe-message.model';
import { ChatViewService } from '../../chat-view.service';
import { KatexOptions } from 'ngx-markdown';
import { MessageService } from 'primeng/api';
import { environment } from '../../../environments/environment';
import { ErrorService } from '../../services/error.service';
import { CompanyChatService } from 'src/app/company-chat.service';

@Component({
  selector: 'app-chat-messages',
  templateUrl: './chat-messages.component.html',
  styleUrl: './chat-messages.component.scss'
})
export class ChatMessagesComponent implements AfterViewInit, OnDestroy {

  @ViewChild("chatContainer") chatContainer: ElementRef<HTMLElement>;
  @HostListener('document:click', ['$event'])

  onDocumentClick(event: MouseEvent): void {
    if (!this.chatViewService.isAssistant()) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.tagName.toLowerCase() === 'a') {
      if ((target as HTMLAnchorElement).href.includes('download%%')) {
        this.downloadFile(event, (target as HTMLAnchorElement));
      }
    }
  }

  domObserver: MutationObserver;
  katexOptions: KatexOptions;

  protected readonly role = MessageRole;
  protected readonly maxLengthOfCitationTitle = 42;

  currentDialogIndex: number | null = null;
  currentMessageNumber: number | null = null;
  currentMouseX: number = 0;
  currentMouseY: number = 0;
  isHoveringCitation: boolean = false;
  isHoveringDialog: boolean = false;
  hideDialogTimeout: any = null;
  showDialogTimeoutId: number | null = null;

  showDialog(messageNumber: number, citationNumber: number) {
  const dialogEl = document.getElementById(`dialog-${messageNumber}-${citationNumber}`);
    if (this.showDialogTimeoutId !== null) {
      clearTimeout(this.showDialogTimeoutId);
      this.showDialogTimeoutId = null;
    }

    this.showDialogTimeoutId = window.setTimeout(() => {
      if (dialogEl) {
        const chatContainer = document.getElementById('chat-container');
        const citationBoundingRect = document.elementFromPoint(this.currentMouseX, this.currentMouseY)?.getBoundingClientRect();

        if (!citationBoundingRect) return;

        const isTopHalf = this.currentMouseY < window.innerHeight / 2;

        dialogEl.style.setProperty('--dialog-left', `${chatContainer.getBoundingClientRect().x}px`);
        dialogEl.style.setProperty('--dialog-width', `${chatContainer.clientWidth}px`);
        dialogEl.style.setProperty('--dialog-top', isTopHalf ? `${citationBoundingRect.y + citationBoundingRect.height}px`: `${citationBoundingRect.y}px`);
        dialogEl.style.setProperty('--dialog-transform', isTopHalf ? 'translate(0%, 0%)' : 'translate(0%, -100%)');
        this.currentDialogIndex = this.isHoveringCitation ? citationNumber : null;
      }
    }, 500);
  }

  citationDialogHover(citationNumber: number, hover:boolean) {
    this.isHoveringDialog = hover;
    this.updateDialogVisibility(citationNumber);
  }

  citationHover(messageNumber : number, citationNumber: number, hover:boolean){
    this.isHoveringCitation = hover
    if(this.isHoveringCitation)
      this.showDialog(messageNumber, citationNumber);
    else{
      this.startHideDialogTimer();
    }
  }

  startHideDialogTimer() {
    clearTimeout(this.hideDialogTimeout);
    this.hideDialogTimeout = setTimeout(() => {
      if (!this.isHoveringCitation && !this.isHoveringDialog) {
        this.currentDialogIndex = null;
        }
      }, 150);  // time before the dialog is hidden
  }

  updateDialogVisibility(citationNumber: number) {
    if (this.isHoveringCitation || this.isHoveringDialog) {
      this.currentDialogIndex = citationNumber;
    } else {
      this.startHideDialogTimer();
    }
  }


  get messages(): FeMessageModel[] {
    return this.chatViewService.messages;
  }

  get aiTypingResponse() {
    return this.chatViewService.aiTypingResponse;
  }

  get aiResponseError() {
    return this.chatViewService.aiResponseError;
  }

  constructor(
    public chatViewService: ChatViewService,
    private readonly assistantService: AssistantService,
    private readonly errorService: ErrorService,
    private readonly messageService: MessageService,
    private readonly daiseService: CompanyChatService,
    private readonly indexService: IndexService,
  ) {
    this.katexOptions = {
      throwOnError: false,
      displayMode: true,
      delimiters: [{left: "$$", right: "$$", display: true}, {left: "$", right: "$", display: false}]
    }
  }

  ngAfterViewInit(): void {
    this.observeChangesInDom();
  }

  ngOnDestroy(): void {
    this.domObserver.disconnect();
  }

  showSendAgain(i: number) {
    return (this.aiResponseError && i === this.messages.length - 1) || (this.chatViewService.stopped && i === this.messages.length - 2);
  }

  resend() {
    this.chatViewService.resend$.next();
  }

  getFormattedTextareaValue(text: string): string {
    text = text.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return text;
  }

  async copyToClipboard(message: string) {
    message = message.replace(/^```|```$/g, '');
    await navigator.clipboard.writeText(message);
    this.messageService.add({
      severity: 'success',
      detail: 'Text copied to clipboard',
      summary: 'Copied!',
    });
  }

  hasUsedCitation(model: FeMessageModel): boolean {
    return model.citations?.some(c => c.used);
  }

  downloadFile(event: MouseEvent, link: HTMLAnchorElement) {
    if (link.href.includes('download%%')) {
      event.preventDefault();
      const regex = /download%%(.*?)%%/;
      const match = regex.exec(link.href)
      const fileId = match[1];
      this.assistantService.getGeneratedFileToken(fileId).subscribe({
        next: response => {
          const fileUrl = `${ environment.apiUrl }/assistant/generated-file/${ fileId }/download?token=${ response.token }`;
          const a = document.createElement('a');
          a.href = fileUrl;
          a.download = fileId;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(fileUrl);
        },
        error: err => {
          this.errorService.handleError(err, 'Could not get file.');
        }
      });
    }
  }

  downloadFileFromAzure(citation: CitationModel, event : Event): void {
    event.preventDefault();
    this.indexService.getFileToken(this.daiseService.daiseIndex.id, citation.hash).subscribe({
      next: (response) => {
        const blobUrl = `${ environment.apiUrl }/indexes/${ this.daiseService.daiseIndex.id }/files/${ citation.hash }/download?token=${ response.token }`
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = citation.title;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      },
      error: (err) => {
        this.errorService.handleError(err, 'Could not download file.');
      }
  });}

  observeChangesInDom() {
    const targetNode = document.getElementById('chat-container');
    const callback = (mutationsList: MutationRecord[]) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node instanceof HTMLElement) {
              this.insertHeaderToMarkdownCodeSnippet(node);
              const children = Array.from(node.children);
              for (const child of children) {
                this.makeAnchorTagsTargetBlank(child);
              }
            }
          });
        }
      }
    };
    this.domObserver = new MutationObserver(callback);
    this.domObserver.observe(targetNode, {childList: true, subtree: true});
  }

  private insertHeaderToMarkdownCodeSnippet(node: HTMLElement) {
    if (node.nodeName !== 'PRE') {
      return;
    }
    const languageClass = Array.from(node.classList).find(className =>
      className.startsWith('language-')
    );

    if (languageClass && !node.querySelector('.markdown-code-header')) {
      node.style.paddingTop = '0';
      const languageName = languageClass?.split('-')[1];
      const markdownCodeHeader = document.createElement('div');
      markdownCodeHeader.classList.add('markdown-code-header');
      markdownCodeHeader.innerText = languageName === 'none' ? 'code snippet' : this.mapLanguageName(languageName);
      node.insertBefore(markdownCodeHeader, node.firstChild);
      const button = node.parentElement.getElementsByClassName('markdown-clipboard-button')[0] as HTMLElement;
      if (button) {
        button.onclick = async (event) => {
          event.preventDefault();
          await navigator.clipboard.writeText(node.getElementsByTagName('code')[0].textContent);
        };
      }
    }
  }

  private makeAnchorTagsTargetBlank(node: Node) {
    if (node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName === 'A') {
      (node as HTMLAnchorElement).setAttribute('target', '_blank');
    } else if (node.hasChildNodes()) {
      const children = Array.from(node.childNodes);
      for (const child of children) {
        this.makeAnchorTagsTargetBlank(child);
      }
    }
  }

  private mapLanguageName(languageName: string) {
    switch (languageName) {
      case 'cpp':
        return 'c++';
      case 'csharp':
        return 'c#';
      default:
        return languageName;
    }
  }

}
